import { takeLatest, takeEvery, delay } from 'redux-saga'
import callAny from './helpers/call_any'
import actionTypes from 'redux/actions/actionTypes'
import { gameSync, assetSync, customSlideSync } from 'sync'
import gameActions from 'redux/actions/gameActions'
import { put, call, select, take, spawn, actionChannel, all, fork } from 'redux-saga/effects'
import gamePendingActivities from 'redux/selectors/game/game_creation_tool_pending_activities_selector'
import gameActivityData from 'redux/selectors/game/game_creation_tool_activity_data'
import gameDataSelector from 'redux/selectors/game/game_creation_tool_data'
import mediaAnswerSubmissionSelector from 'redux/selectors/game/game_creation_tool_media_submit_selector'
import gameSaveValidSelector from 'redux/selectors/game/game_creation_tool_save_validation'
import gameActivitiesSelector from 'redux/selectors/game/game_creation_tool_activities_selector'
import customSlideIdSelector from 'redux/selectors/activity_creation/custom_slide_id_selector'
import slideOriginSelector from 'redux/selectors/slide/slide_origin_selector'
import formatError from './helpers/error_formatter'
import { localize } from 'helpers/translator'
import mixpanel from 'helpers/mixpanel'
import isCurrentActivityModified from 'redux/selectors/activities/is_current_activity_modified'
import activityOrigins from 'helpers/enums/activities/activityOrigins'

function *getSlideData() {
	const customSlideId = yield select(customSlideIdSelector)
	const slideData = yield callAny(
		customSlideSync.getSlide,
		customSlideId,
	)
	yield put(gameActions.initializeSlideDataSuccess(slideData.payload))
}

function *getGameData(action) {
	try {
		yield fork(getSlideData)
		const gameData = yield callAny(gameSync.getGame, action.gameUID)
		// @Todo: Remove when possible. Activities modified to support new format
		const activities = gameData.payload.game.activities
		activities.forEach(activity => {
			activity.answers.forEach(answer => {
				if (answer.text && !answer.media) {
					answer['media'] = {
						type: 'text',
						payload: {
							text: answer.text,
						},
					}
				}
			})
		})
		////
		yield put(gameActions.initializeDataSuccess(gameData.payload))
		yield spawn(addActivityListeners)
	} catch (error) {
		// console.log(error)
	}
}

function *addQuestion(action) {
	if (!action._isCancelled) {
		try {
			const gameData = yield select(gameDataSelector)
			yield spawn(startQuestionInstanceListener, action.clientId)
			const questionData = yield callAny(gameSync.addQuestion, gameData.uid)
			yield put(
				gameActions.activityCreated(
					action.clientId,
					questionData.payload.uid,
					questionData.payload.entityUid,
				)
			)
		} catch (error) {
			// console.log(error)
		}
	}
}

function *duplicateQuestion(action) {
	if (!action._isCancelled) {
		try {
			const gameData = yield select(gameDataSelector)
			const originActivityData = yield call(getActivityStatus, action.activityId)
			const newActivityData = yield select(gameActivityData, action.clientId)
			yield spawn(startQuestionInstanceListener, action.clientId)
			const questionData = yield callAny(gameSync.duplicateQuestion, gameData.uid, originActivityData.serverId)
			yield put(gameActions.activityDuplicated(newActivityData, questionData.payload))
		} catch (error) {
			// console.log(error)
		}
	}
}

function *getActivityStatus(activityId) {
	let activityData
	while (!activityData || !activityData.serverId) {
		activityData = yield select(gameActivityData, activityId)
		if (!activityData.serverId) {
			yield take([
				actionTypes.GAME_ACTIVITY_CREATED_SUCCESS,
				actionTypes.GAME_QUESTION_DUPLICATE_SUCCESS,
			])
		}
	}
	return activityData
}

function *deleteActivity(action) {
	try {
		const activityData = yield call(getActivityStatus, action.activityId)
		const gameData = yield select(gameDataSelector)
		yield callAny(gameSync.deleteActivity, gameData.uid, activityData.serverId)

		yield put(gameActions.activityDeleted(action.activityId))
	} catch (error) {
		// console.log(error)
	}
}

function *addActivityListeners() {
	const activities = yield select(gameActivitiesSelector)
	for (const activity of activities) {
		yield spawn(startQuestionInstanceListener, activity.id)
	}
}

function *saveQuestion(activityId) {
	let activityData
	try {
		activityData = yield call(getActivityStatus, activityId)
		if (activityData.state !== 'deleting') {
			const gameData = yield select(gameDataSelector)
			const activityDataQuery = {
				...activityData,
				answers: activityData.answers.map(answer => ({
					correct: answer.correct,
					text: answer.media ? answer.media.payload.text : answer.text,
					media: answer.media,
					uid: answer.serverId,
				})),
			}
			const saveResponse = yield callAny(gameSync.saveActivity, gameData, activityDataQuery)
			// Before we dispatch the activity saved action, we check if there has been a new modification to the activity
			// This way we don't set the activity state as ready when there is still one modification pending
			const newActivityData = yield call(getActivityStatus, activityId)
			if (activityData.lastModified === newActivityData.lastModified) {
				yield put(gameActions.activitySaved(activityId, saveResponse.payload.answers))
			}
		}
	} catch (err) {
		const newActivityData = yield call(getActivityStatus, activityId)
		if (activityData.lastModified === newActivityData.lastModified) {
			yield put(gameActions.activitySaveFailed(activityId))
		}
	}
}

function savingActivityBuffer(activityId) {
	let currentAction = null

	return {
		put: action => {
			if (action.activityId === activityId
				&& !action._isCancelled
				&& (!currentAction || currentAction.type !== actionTypes.GAME_DELETE_ACTIVITY_CONFIRMED)) {
				currentAction = action
			}
		},
		take: () => {
			const dispatchingAction = currentAction
			currentAction = null
			return dispatchingAction
		},
		flush: () => {
			currentAction = null
		},
		isEmpty: () => !currentAction,
	}
}

function *startQuestionInstanceListener(activityId) {

	const activityActions = [
		actionTypes.GAME_QUESTION_ANSWER_CORRECT_TOGGLE,
		actionTypes.GAME_QUESTION_ANSWER_DELETE,
		actionTypes.GAME_QUESTION_ANSWER_DROP,
		actionTypes.GAME_QUESTION_ADD_ANSWER,
		actionTypes.GAME_DELETE_ACTIVITY_CONFIRMED,
		actionTypes.GAME_QUESTION_ANSWER_SAVE,
		actionTypes.GAME_QUESTION_TIMER_SAVE,
		actionTypes.GAME_ACTIVITY_CREATED_SUCCESS,
		actionTypes.GAME_QUESTION_SELECT_ANSWER_TYPE,
		actionTypes.GAME_UPLOAD_ANSWER_MEDIA_DELETE,
		actionTypes.GAME_UPLOAD_ANSWER_MEDIA_FAILED,
	]

	try {
		let isQuestionActive = true
		const activityChan = yield actionChannel(activityActions, savingActivityBuffer(activityId))
		while (isQuestionActive) {
			const action = yield take(activityChan)
			if (action.activityId === activityId) {
				if (action.type === actionTypes.GAME_DELETE_ACTIVITY_CONFIRMED) {
					isQuestionActive = false
				} else {
					yield call(saveQuestion, activityId)
				}
			}
		}
	} catch (error) {
		// console.log(error)
	}
}

function *trackSavingGame(gameData) {
	try {

		const typesMap = {
			image: 'Image',
			text: 'Text',
		}

		const properties = gameData.activities.reduce((accumulator, activity) => {
			const answerType = activity.answers[0].media.type
			const trackingTypes = typesMap[answerType] || 'Not Defined'
			if (!accumulator[trackingTypes]) {
				accumulator[trackingTypes] = 0
			}
			accumulator[trackingTypes] += 1
			return accumulator
		}, {})

		yield call(mixpanel.track, 'Time To Climb Save', properties, true)
	} catch (err) {

	}
}

function *trackActivitySaved(gameData) {
	const hasReferenceMedia = !!gameData.activities.find(activity => activity.icon.id !== 0)
	const wasActivityEdited = yield select(isCurrentActivityModified)
	const slideOrigin = yield select(slideOriginSelector, gameData.slideId)
	const properties = {
		Activity: 'Time to Climb',
		'Reference Media': hasReferenceMedia,
		'Reference Media Type': hasReferenceMedia ? 'Image' : '',
		'Activity Bank Content Edited': slideOrigin === activityOrigins.ACTIVITY_LIBRARY && wasActivityEdited,
		'Activity Origin': slideOrigin,
	}
	yield call(mixpanel.track, 'Activity Saved', properties, true)
}

function *saveGame() {
	try {
		let validationSelector = yield select(gameSaveValidSelector)
		if (validationSelector.valid) {
			let hasPendingActivities = true
			while (hasPendingActivities) {

				// We are validating on each iteration in case the activity is no longer valid to save.
				// For example when an image upload fails
				validationSelector = yield select(gameSaveValidSelector)
				if (!validationSelector.valid) {
					throw new Error()
				}
				//TODO: activities should be in the process of saving at this point,
				// but perhaps we want to make sure there are no idle activities that need to be forced to be saved
				hasPendingActivities = yield select(gamePendingActivities)

				if (hasPendingActivities) {
					yield call(delay, 500)
				}
			}
			const gameData = yield select(gameDataSelector)
			yield spawn(trackSavingGame, gameData)
			yield callAny(gameSync.saveGame, gameData.uid, gameData.timer)
			yield spawn(trackActivitySaved, gameData)
			yield put(gameActions.gameSaveSuccess())
		} else {
			const error = new Error()
			error.serverError = {error_code: -10}
			throw error
		}
	} catch (error) {
		const serverError = formatError(error)
		yield put(gameActions.gameSaveFailed(serverError))
	}
}

function *getSubmissionData(action) {

	let count = 0

	let submissionData = yield select(mediaAnswerSubmissionSelector, action.questionId, action.answerId)

	while (!submissionData.answerUid || !submissionData.activityUid) {
		submissionData = yield select(mediaAnswerSubmissionSelector, action.questionId, action.answerId)
		yield call(delay, 100)
		count += 1

		//This attempts 50 times to get the answer id (~5s), if it fails it will consider the upload as failed
		if (count >= 50) {
			throw new Error()
		}
	}

	return submissionData

}

function *gameUploadAnswerImage(action) {
	try {
		const submissionData = yield call(getSubmissionData, action)
		const file = action.files.files[0]

		const response = yield callAny(assetSync.upload, file, submissionData.gameUid, submissionData.activityUid, submissionData.answerUid)
		yield put(gameActions.answerMediaUploadSuccess(action.questionId, action.answerId, response.payload))

	} catch (error) {
		yield put(gameActions.answerMediaPickerFailed(action.questionId, action.answerId))
	}
}

function *gameSortQuestions() {
	try {
		const game = yield select(gameDataSelector)
		const activities = yield all(game.activities.map(activity => call(getActivityStatus, activity.id)))
		const activitiesUids = activities.map(activity => activity.serverId)
		yield callAny(gameSync.sortQuestions, game.uid, activitiesUids)
		yield put(gameActions.sortQuestionsSuccess())
	} catch (error) {
		yield put(gameActions.sortQuestionsFailed())
	}
}

function *gameUploadQuestionReferenceMedia(action) {
	try {
		const questionData = yield select(gameActivityData, action.questionId)
		const gameData = yield select(gameDataSelector)
		const file = action.files.files[0]
		const uploadResponse = yield callAny(
			assetSync.uploadQuestionImage,
			file,
			gameData.uid,
			questionData.questionUid
		)
		yield put(
			gameActions.questionMediaPickerSuccess(
				action.questionId,
				uploadResponse.payload.asset,
			)
		)
	} catch (error) {
		const serverError = formatError(error)
		yield put(
			gameActions.questionMediaPickerFailed(
				serverError,
				localize('global_generic_error'),
				action.questionId,
			)
		)
	}
}

function *gameDeleteQuestionReferenceMedia(action) {
	try {
		const questionData = yield select(gameActivityData, action.questionId)
		const gameData = yield select(gameDataSelector)
		yield callAny(
			gameSync.deleteQuestionReferenceMedia,
			gameData.uid,
			questionData.questionUid,
		)
		yield put(gameActions.deleteQuestionMediaSuccess(action.questionId))
	} catch (error) {
		const serverError = formatError(error)
		yield put(
			gameActions.deleteQuestionMediaFailed(
				serverError,
				localize('global_generic_error'),
				action.questionId,
			)
		)
	}
}

export default [
	takeLatest([
		actionTypes.GAME_INITIALIZE_CREATION_TOOL,
		actionTypes.GAME_UPDATE_CREATION_TOOL,
		actionTypes.GAME_INITIALIZE_STANDALONE,
	], getGameData),
	takeEvery(actionTypes.GAME_ADD_QUESTION, addQuestion),
	takeEvery(actionTypes.GAME_QUESTION_DUPLICATE, duplicateQuestion),
	takeLatest(actionTypes.GAME_DELETE_ACTIVITY_CONFIRMED, deleteActivity),
	takeLatest(actionTypes.GAME_SAVE, saveGame),
	takeEvery(actionTypes.GAME_UPLOAD_ANSWER_MEDIA_SELECTED, gameUploadAnswerImage),
	takeLatest(actionTypes.GAME_DROP_QUESTION, gameSortQuestions),
	takeEvery(actionTypes.GAME_UPLOAD_QUESTION_MEDIA_SELECTED, gameUploadQuestionReferenceMedia),
	takeLatest(actionTypes.GAME_UPLOAD_QUESTION_MEDIA_DELETE, gameDeleteQuestionReferenceMedia),
]

export {
	trackActivitySaved,
	getSlideData,
}
